import { WhatsAppMessage } from '@/types/whatsapp';

function parseWhatsAppDate(dateStr: string): Date {
  // Try different date formats
  const formats = [
    // DD/MM/YY, HH:mm
    /(\d{1,2})\/(\d{1,2})\/(\d{2}),\s*(\d{1,2}):(\d{2})/,
    // DD/MM/YYYY, HH:mm
    /(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})/,
    // MM/DD/YY, HH:mm
    /(\d{1,2})\/(\d{1,2})\/(\d{2}),\s*(\d{1,2}):(\d{2})/,
    // MM/DD/YYYY, HH:mm
    /(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})/
  ];

  for (const format of formats) {
    const match = dateStr.match(format);
    if (match) {
      const [, day, month, year, hours, minutes] = match;
      // Convert 2-digit year to 4-digit
      const fullYear = year.length === 2 ? '20' + year : year;
      // Create date object (months are 0-based in JavaScript)
      try {
        const date = new Date(
          parseInt(fullYear),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hours),
          parseInt(minutes)
        );
        if (!isNaN(date.getTime())) {
          return date;
        }
      } catch (e) {
        continue;
      }
    }
  }
  throw new Error('Invalid date format in chat message');
}

export async function parseWhatsAppChat(file: File): Promise<WhatsAppMessage[]> {
  try {
    const text = await file.text();
    const lines = text.split('\n');
    const messages: WhatsAppMessage[] = [];
    
    // Updated regex to better handle WhatsApp chat format variations
    const messageRegex = /\[?(\d{1,2}\/\d{1,2}\/(?:\d{2}|\d{4}),\s*\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AaPp][Mm])?)\]?\s*[-:]?\s*([^:]+):\s*(.+)/;

    for (const line of lines) {
      const match = line.match(messageRegex);
      if (match) {
        const [, timestamp, sender, content] = match;
        let messageDate;
        try {
          messageDate = parseWhatsAppDate(timestamp);
        } catch (error) {
          console.warn('Skipping message with invalid date:', timestamp);
          continue;
        }

        messages.push({
          timestamp: messageDate,
          sender: sender.trim(),
          content: content.trim(),
        });
      }
    }

    if (messages.length === 0) {
      throw new Error('No valid messages found in the chat file');
    }
    return messages;
  } catch (error) {
    console.error('Error parsing WhatsApp chat:', error);
    throw new Error(
      error instanceof Error 
        ? error.message 
        : 'Failed to parse WhatsApp chat file. Please ensure it\'s a valid export.'
    );
  }
}

export function validateWhatsAppFile(file: File): boolean {
  // Check file type
  if (file.type !== 'text/plain') {
    return false;
  }

  // Check file size (max 10MB)
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > MAX_FILE_SIZE) {
    return false;
  }

  return true;
}